import React, { forwardRef, useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { AnnouncementList } from "Shared/Types/fleetmanagementapi";
import styles from "../../styles/Announcements.module.scss";
import { useAnnouncements } from "Components/Announcements/useAnnouncementsModal";
import { StatusBadge } from "Components/UI/DataTable";
import { cropText, formatToRelativeTime } from "Shared/Utils/shared";
import cn from "classnames";
import { CalendarIcon } from "Assets";
import Line from "Components/UI/Line/Line";
import { useModal } from "@ebay/nice-modal-react";
import ImageShowModal from "Components/ImageShowModal/ImageShowModal";

type AnnouncementItemProps = {
  announcement: AnnouncementList;
};

const AnnouncementItem = forwardRef<
  HTMLLIElement | null,
  AnnouncementItemProps
>(({ announcement }, ref) => {
  // Long descriptions start cut to 160 characters.
  const [collapsed, setCollapsed] = useState(
    announcement.description.length > 160,
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const photo = imageFailed ? undefined : announcement.photos[0]?.file;
  const modal = useModal(ImageShowModal);
  const imgRef = useRef(null);
  const itemRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (itemRef.current) {
      const tl = gsap.timeline();

      tl.to(itemRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.6,
        ease: "back.out(1.7)",
        delay: 1,
      });
    }
  }, []);

  const toggleDescription = () => {
    setCollapsed((prev) => !prev);
  };

  const onShowImage = (src: string) => {
    modal.show({ src });
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    if (isLoaded && imgRef.current) {
      gsap.fromTo(imgRef.current, { opacity: 0 }, { opacity: 1, duration: 5 });
    }
  }, [isLoaded]);

  const { getCategoryTone } = useAnnouncements();

  return (
    <li
      className={cn(styles.announcementWrapper__announcement)}
      key={announcement.id}
      ref={(el) => {
        itemRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      }}
    >
      <header className={styles.announcementWrapper__announcement__header}>
        <div
          className={
            styles.announcementWrapper__announcement__header__categoryDate
          }
        >
          <StatusBadge tone={getCategoryTone(announcement.category)}>
            {announcement.category}
          </StatusBadge>
          <div
            className={
              styles.announcementWrapper__announcement__header__categoryDate__date
            }
          >
            <h4
              className={
                styles.announcementWrapper__announcement__header__categoryDate__date__text
              }
            >
              {formatToRelativeTime(announcement.date)}
            </h4>
            <CalendarIcon width={"20px"} />
          </div>
        </div>
        <h4 className={styles.announcementWrapper__announcement__header__title}>
          {announcement.title}
        </h4>
        <Line />
      </header>

      {photo && (
        <img
          ref={imgRef}
          className={styles.announcementWrapper__announcement__image}
          onClick={() => onShowImage(photo)}
          src={photo}
          alt={announcement.title}
          onLoad={handleImageLoad}
          onError={() => setImageFailed(true)}
        />
      )}

      <footer>
        <p
          className={
            styles.announcementWrapper__announcement__footer__description
          }
        >
          {collapsed
            ? cropText({ value: announcement.description, maxLength: 160 })
            : announcement.description}
        </p>
        {collapsed && (
          <button
            type="button"
            className={styles.textButton}
            onClick={toggleDescription}
          >
            Read more
          </button>
        )}
      </footer>
    </li>
  );
});

AnnouncementItem.displayName = "AnnouncementItem";

export default AnnouncementItem;
